import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useMutation, useQuery } from "@tanstack/react-query";

import MDBox from "components/MDBox";
import { Success, Error } from "ui-components/Alert";
import { formRender, initialValues, stockTransferValidation } from "./stock-transfer-schema";
import {
  formRender as detailRender,
  initialValues as detailValues,
  stockTransferDetailValidation,
} from "./stock-transfer-detail-schema";
import StockTransferInfo from "./components/StockTransferInfo";
import { inventoryColumns } from "./components/columns";
import MDTypography from "components/MDTypography";
import { StripedDataGrid } from "layouts/views/components/Grid";
import { useCustomerValue } from "providers/CustomerContext";
import { createStockTransfer } from "services/data.inventory.service";
import StockTransferDetailInfo from "./components/StockTransferDetailInfo";
import MDButton from "components/MDButton";
import styled from "@emotion/styled";
import { InboundSaveButton } from "ui-components/Form";
import dayjs from "dayjs";
import { Fieldset } from "ui-components/Form";
import { InboundLayout } from "ui-components/Layout";
import {
  Autocomplete,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { GridFooterContainer, useGridApiContext } from "@mui/x-data-grid";
import { useLocationOptionsQuery } from "hooks/useLocationQuery";
import { useZoneOptionsQuery } from "hooks/useZoneQuery";
import { getPalletOptions } from "services/data.inventory.service";
import { EditableSelectCustomOnChange } from "ui-components/EditableInput2";
import { AddButton } from "ui-components/IconButton";
import { DeleteButton } from "ui-components/IconButton";
import { getCommonInventoryList } from "services/data.inventory.service";
import { StockTransferCaption } from "../components/Caption";
import { FormTitle } from "ui-components/Form";
import { LoadingDialog } from "ui-components/Alert";
import { LoadingAlert } from "ui-components/Alert";
import { Error_ } from "ui-components/Alert";
import { getShowDivision } from "services/data.inbound.service";
import TransferDivisionGroup from "./components/TransferDivisionGroup";

const defaultDetailValues = {
  ...detailValues,
  holdPallet: { label: "All pallet", value: "A" },
};

const isCellEditable = (params) => {
  if (params.field === "transferQuantity") {
    return (
      params.row.toZone !== null &&
      params.row.toZone !== "" &&
      params.row.toLocation !== null &&
      params.row.toLocation !== ""
    );
  }
  if (params.field === "toPallet") {
    return (
      params.row.toZone !== null &&
      params.row.toZone !== "" &&
      params.row.toLocation !== null &&
      params.row.toLocation !== "" &&
      params.row.transferQuantity !== null &&
      params.row.transferQuantity !== 0
    );
  }
  if (
    params.field === "toCartonID" ||
    params.field === "toMLN" ||
    params.field === "toExpiryDate"
  ) {
    return (
      params.row.toZone !== null &&
      params.row.toZone !== "" &&
      params.row.toLocation !== null &&
      params.row.toLocation !== "" &&
      params.row.transferQuantity !== null &&
      params.row.transferQuantity !== 0 &&
      params.row.toPallet === "New"
    );
  }
  if (params.field === "toZone") {
    return true;
  }
  if (params.field === "toLocation") {
    return params.row.toZone !== null && params.row.toZone !== "";
  }
  return params.editable || false;
};

const EditableStyledDataGrid = styled(StripedDataGrid)({
  ".custom-editable-header": {
    backgroundColor: "#1A73E8 !important",
    color: "#fff",
    fontWeight: "bold",
    whiteSpace: "normal !important",
  },
  ".MuiDataGrid-columnHeaderTitle": {
    whiteSpace: "normal !important",
    lineHeight: 1,
  },
  ".MuiDataGrid-columnHeader": {
    backgroundColor: "#f0f2f5",
  },
  ".MuiDataGrid-cell": {
    whiteSpace: "normal !important",
    "word-wrap": "break-word !important",
  },
  ".right-align-header": {
    textAlign: "right",
  },
  ".highlight": {
    backgroundColor: "pink !important",
  },
});

function deepCopy(obj) {
  if (obj === null || typeof obj !== "object") {
    // If the object is a primitive or null, return it as is
    return obj;
  }

  if (obj instanceof Date) {
    // If the object is a Date, create a new Date with the same value
    return new Date(obj.getTime());
  }

  if (Array.isArray(obj)) {
    // If the object is an array, create a new array and deep copy its elements
    return obj.map((element) => deepCopy(element));
  }

  // If the object is a regular object, create a new object and deep copy its properties
  const copiedObject = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      copiedObject[key] = deepCopy(obj[key]);
    }
  }

  return copiedObject;
}

const CustomDatePicker = (props) => {
  const [selectedDate, setSelectedDate] = useState(props.value ? dayjs(props.value) : null);

  const apiRef = useGridApiContext();

  const handleDateChange = async (date, context) => {
    if (context.validationError == null) {
      setSelectedDate(date);
      await apiRef.current.setEditCellValue({
        id: props.id,
        field: props.field,
        value: date ? date.$d : null,
      });
    } else {
      setSelectedDate(null);
      await apiRef.current.setEditCellValue({
        id: props.id,
        field: props.field,
        value: null,
      });
    }
  };

  return (
    <DatePicker
      value={selectedDate}
      autoFocus
      // disablePast
      onChange={handleDateChange}
      format="DD/MM/YYYY"
      slotProps={{
        field: { clearable: true },
      }}
    />
  );
};

function AddStockTransfer() {
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const { customerCode } = useCustomerValue();
  const errorColumns = [
    {
      field: "id",
      headerName: "ID",
      sortable: false,
      align: "center",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "locId",
      headerName: "LocAvaiID",
      sortable: false,
      align: "center",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "locationID",
      headerName: "Location ID",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "skuCode",
      headerName: "SKU Code",
      sortable: false,
      align: "left",
      flex: 1.8,
      minWidth: 180,
    },
    {
      field: "palletNbr",
      headerName: "Pallet Nbr",
      sortable: false,
      align: "left",
      flex: 1.2,
      minWidth: 112,
    },
    {
      field: "cartonID",
      headerName: "Carton ID",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "mln",
      headerName: "MLN",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "expiryDate",
      headerName: "Expiry Date",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "message",
      headerName: "Message",
      sortable: false,
      align: "left",
      flex: 1.6,
      minWidth: 160,
    },
  ];

  const [detail, setDetail] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loadEnabled, setLoadEnabled] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [zone, setZone] = useState(null);
  const [loc, setLoc] = useState(null);
  const [sku, setSKU] = useState([]);
  const [l, setL] = useState({});
  const [p, setP] = useState({});
  const [pallets, setPallets] = useState({});

  const [errorItems, setErrorItems] = useState([]);
  const [uploadData, setUploadData] = useState([]);
  const [showDivision, setShowDivision] = useState(false);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const createStockTransferMutation = useMutation(createStockTransfer, {
    onSuccess: (data) => {
      setDialog(null);
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError: (data) => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      const errorData = data.response.data.data;
      if (dialog) setDialog(null)
      if (errorData) {
        setDialog(
          <Dialog open={true} maxWidth={"lg"}>
            <DialogTitle>{data.response.data.message}</DialogTitle>
            <DialogContent>
              <EditableStyledDataGrid
                columns={errorColumns}
                rows={errorData}
                sx={{
                  borderRadius: "0.75rem",
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                getRowHeight={() => "auto"}
                hideFooterSelectedRowCount={true}
              />
            </DialogContent>

            <DialogActions style={{ display: "flex", justifyContent: "center" }}>
              <MDButton
                color="info"
                style={{ width: "4rem" }}
                onClick={() => {
                  setDialog(null);
                }}
              >
                Ok
              </MDButton>
            </DialogActions>
          </Dialog>
        );

        //might return from destination pallet, do by pallet nbr
        const errorDetail = detail.filter((row) =>
          errorData.some((err) => err.palletNbr === row.palletNbr || err.palletNbr === row.toPallet)
        );
        const remainingDetail = detail.filter(
          (row) => !errorDetail.some((err) => err.locId === row.locId)
        );
        setDetail(remainingDetail);

        const errorLocId = [...new Set(errorDetail.map((err) => err.locId))];
        const errorLocIdObject = errorLocId.map((el) => {
          const newId = el + "-1";
          return { id: newId };
        });
        setErrorItems(errorLocIdObject);
      }
      setLoadEnabled(true);
      setAlert(<Error data={data} onChange={setAlert} />);
    },
  });

  const CustomPicker = (props) => {
    const { options, ...params } = props;
    const [selectedVal, setSelectedVal] = useState(params.value ? params.value : null);

    const apiRef = useGridApiContext();

    const handleValChange = async (e, value) => {
      setSelectedVal(value);
      await apiRef.current.setEditCellValue({
        id: params.id,
        field: params.field,
        value: value,
      });
    };

    return (
      <EditableSelectCustomOnChange
        getValue={() => selectedVal}
        onChange={handleValChange}
        options={options}
        style={{ width: "100%" }}
      />
    );
  };

  const defaultValues = {
    ...initialValues,
    customerCode,
  };

  const form = useForm({
    defaultValues: defaultValues,
    values: defaultValues,
    resolver: yupResolver(stockTransferValidation),
  });

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
    trigger,
    reset,
    setValue,
  } = form;

  const [warehouseCode, fromWarehouseCode, uploadDivision] = watch(["warehouseCode", "fromWarehouseCode", "uploadDivision"]);

  useEffect(() => {
    setDetail([]);
    setInventory([]);
  }, [fromWarehouseCode, warehouseCode]);

  const [zoneOpt, setZoneOpt] = useState([]);

  useQuery({
    queryKey: ["show-division", customerCode],
    queryFn: () => getShowDivision({ customerCode }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => setShowDivision(data.data.data),
    onError,
  });

  useZoneOptionsQuery({
    whCode: warehouseCode,
    zoneType: "Storage/Quarantine/Return/Scrap",
    onSuccess: (data) => {
      let result = data.data.data;
      setZoneOpt(result);
    },
  });

  useLocationOptionsQuery({
    whCode: warehouseCode,
    zoneID: zone,
    enabled: Boolean(zone),
    onSuccess: (data) => {
      setL({ ...l, [zone]: data.data.data });
    },
  });

  useQuery({
    queryKey: ["pallet-list", { customerCode, warehouseCode, zone, loc, sku }],
    queryFn: () =>
      getPalletOptions({
        customerCode: customerCode,
        warehouseCode: warehouseCode,
        zoneID: zone,
        locationID: loc,
        skuCode: sku,
      }),
    enabled:
      Boolean(zone) &&
      Boolean(loc) &&
      sku.length > 0 &&
      (!Boolean(p[loc]) || Boolean(p[loc] && sku.some((s) => !Boolean(p[loc][s])))),
    onSuccess: (data) => {
      const list = data.data.data;
      let spoption = { ...p };
      let spallets = { ...pallets };
      sku.forEach((a) => {
        const s = list.find((sp) => sp.skuCode === a);
        if (s !== undefined) {
          const result = s.pallets.map((x) => x.palletNbr);
          if (spoption[loc] === undefined) {
            spoption = { ...spoption, [loc]: { [s.skuCode]: ["New", "Source", ...result] } };
            spallets = { ...spallets, [loc]: { [s.skuCode]: s.pallets } };
          } else {
            spoption = {
              ...spoption,
              [loc]: { ...spoption[loc], [s.skuCode]: ["New", "Source", ...result] },
            };
            spallets = { ...spallets, [loc]: { ...spallets[loc], [s.skuCode]: s.pallets } };
          }
        } else {
          if (spoption[loc] === undefined) {
            spoption = { ...spoption, [loc]: { [a]: ["New", "Source"] } };
          } else if (spoption[loc][a] === undefined) {
            spoption = { ...spoption, [loc]: { ...spoption[loc], [a]: ["New", "Source"] } };
          }
        }
      });
      setP(spoption);
      setPallets(spallets);
    },
    onError: (data) => {
      sku.forEach((s) => {
        if (p[loc] === undefined) {
          setP({ ...p, [loc]: { [s]: ["New", "Source"] } });
        } else if (p[loc][s] === undefined) {
          setP({ ...p, [loc]: { ...p[loc], [s]: ["New", "Source"] } });
        }
      });
    },
  });

  const onAdd = (row) => {
    const existingRowForCurrent = inventory
      .filter((x) => x.locId == row.locId)
      .sort((a, b) => {
        return a.lineNo - b.lineNo;
      });
    const lastLine = existingRowForCurrent[existingRowForCurrent.length - 1];
    const newLineNo = lastLine.lineNo + 1;
    const index = inventory.findIndex((inv) => inv.id == lastLine.id);
    const totalQuantity = existingRowForCurrent.reduce((accumulator, currentValue) => {
      return accumulator + currentValue.transferQuantity;
    }, 0);

    const newLine = {
      ...row,
      id: row.locId + "-" + newLineNo,
      lineNo: newLineNo,
      locId: row.locId,
      transferQuantity: row.quantityAvailable - (totalQuantity ?? 0),
      toZone: fromWarehouseCode === warehouseCode ? row.zoneID : null,
      toLocation: null,
      toPallet: null,
      toCartonID: null,
      toExpiryDate: null,
    };
    const newInv = deepCopy(inventory);
    newInv.splice(index + 1, 0, newLine);
    setInventory(newInv);
  };

  const customColumn = deepCopy(inventoryColumns.filter(x => x.field !== "warehouseCode" && x.field !== "fromWarehouseCode")).map((x) => {
    if (x.field === "actions") {
      x.getActions = ({ row }) => {
        const existingRowForCurrent = inventory.filter((x) => x.locId == row.locId);
        const totalQuantity = existingRowForCurrent.reduce((accumulator, currentValue) => {
          return accumulator + currentValue.transferQuantity;
        }, 0);
        const allowAdd =
          row.transferQuantity === 0 ||
          row.quantityAvailable - (totalQuantity ?? 0) <= 0 ||
          row.quantityAvailable <= 0;
        return [
          <>
            {row.lineNo === 1 && (
              <AddButton
                title="Add"
                key="add-button"
                onClick={() => {
                  onAdd(row);
                }}
                disabled={allowAdd}
              />
            )}
          </>,
        ];
      };
    }
    if (x.field === "toZone") {
      x.headerClassName = "custom-editable-header";
      x.editable = true;
      x.renderEditCell = (params) => {
        return <CustomPicker options={zoneOpt} {...params} />;
      };
    }
    if (x.field === "toLocation") {
      x.headerClassName = "custom-editable-header";
      x.editable = true;
      x.renderEditCell = (params) => {
        return <CustomPicker options={l[params.row.toZone] ?? []} {...params} />;
      };
    }
    if (x.field === "quantity" || x.field === "quantityAvailable") {
      x.headerClassName = "right-align-header";
    }
    if (x.field === "transferQuantity") {
      x.headerClassName = "custom-editable-header right-align-header";
      x.editable = true;
    }
    if (x.field === "toPallet") {
      x.headerClassName = "custom-editable-header";
      x.editable = true;
      x.renderEditCell = (params) => {
        const rowsForCurrent = inventory.filter((x) => x.locId === params.row.locId);
        const totalQuantity = rowsForCurrent.reduce((accumulator, currentValue) => {
          return accumulator + currentValue.transferQuantity;
        }, 0);
        const palletOptions = Boolean(p[params.row.toLocation]) && Boolean(p[params.row.toLocation][params.row.skuCode])
          ? p[params.row.toLocation][params.row.skuCode].filter(
            (x) =>
              x !== params.row.palletNbr &&
              !rowsForCurrent
                .filter((y) => y.id !== params.row.id && y.toPallet !== "New")
                .some((y) => y.toPallet === x) &&
              (params.row.toLocation === params.row.locationID ||
                params.row.quantityAvailable + params.row.quantityOnHold != params.row.quantity ||
                totalQuantity !== params.row.quantityAvailable ||
                params.row.quantityOnHold !== 0
                ? x !== "Source"
                : true)
          ) ?? []
          : [];
        return <CustomPicker options={palletOptions} {...params} />;
      };
    }
    if (x.field === "toCartonID" || x.field === "toMLN") {
      x.headerClassName = "custom-editable-header";
      x.editable = true;
    }
    if (x.field === "toExpiryDate") {
      x.headerClassName = "custom-editable-header";
      x.editable = true;
      x.renderCell = (params) => {
        if (params.value !== null) {
          const date = new Date(params.value);
          const formattedDate = dayjs(date).format("DD / MM / YYYY");
          return <div>{formattedDate}</div>;
        }
      };
      x.renderEditCell = (params) => <CustomDatePicker {...params} />;
    }
    return x;
  });

  const infoProps = {
    control,
    errors,
    formRender,
    customerCode,
    setValue,
    showDivision,
  };

  const onSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      return;
    }
    setDialog(<LoadingDialog />)

    const { uploadDivision, ...data } = getValues();
    if (Boolean(showDivision) && uploadDivision && !Boolean(uploadData.length)) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setAlert(
        <Error_
          content="Division File Required but No file uploaded"
          onChange={setAlert}
        />
      )
      setDialog(null);
      return;
    }

    const req = {
      ...data,
      stockTransferDetails: detail.map((x) => {
        const { expiryDate, toExpiryDate, ...data } = x;
        return {
          expiryDate: expiryDate ? dayjs(expiryDate, "DD / MM / YYYY").format("YYYY-MM-DD") : null,
          toExpiryDate: toExpiryDate ? dayjs(toExpiryDate).format("YYYY-MM-DD") : null,
          ...data,
        };
      }),
      divisionQuantity: JSON.stringify(uploadData),
      uploadDivision: Boolean(showDivision) && uploadDivision
    };

    createStockTransferMutation.mutate(req, {
      onSuccess: () => {
        reset();
        setUploadData([]);
      },
    });
  };
  const createStatus = createStockTransferMutation.status === "loading";
  return (
    <InboundLayout lg={12}>
      {alert}
      {dialog}
      <Card sx={{ height: "100%" }}>
        <MDBox p={3}>
          <form noValidate>
            <StockTransferInfo {...infoProps} />
          </form>
        </MDBox>
      </Card>
      <MDBox sx={{ margin: "1rem 0" }} />
      <StockTransferDetail
        customerCode={customerCode}
        warehouseCode={warehouseCode}
        inventory={inventory}
        setInventory={setInventory}
        detail={detail}
        setDetail={setDetail}
        loadEnabled={loadEnabled}
        setLoadEnabled={setLoadEnabled}
        customColumn={customColumn}
        setZone={setZone}
        setLoc={setLoc}
        l={l}
        p={p}
        setL={setL}
        setSKU={setSKU}
        errorItems={errorItems}
        setErrorItems={setErrorItems}
        zoneOpt={zoneOpt}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        pallets={pallets}
        fromWarehouseCode={fromWarehouseCode}
        showDivision={showDivision}
        uploadData={uploadData}
        setUploadData={setUploadData}
        uploadDivision={uploadDivision}
      />
      <InboundSaveButton
        label="submit"
        disabled={createStatus || detail.length === 0}
        url="/inventory/stock-transfer"
        onClick={onSubmit}
      />
    </InboundLayout>
  );
}

const StockTransferDetail = ({
  customerCode,
  warehouseCode,
  fromWarehouseCode,
  inventory,
  setInventory,
  detail,
  setDetail,
  loadEnabled,
  setLoadEnabled,
  customColumn,
  setZone,
  setLoc,
  l,
  p,
  setL,
  setSKU,
  errorItems,
  setErrorItems,
  zoneOpt,
  selectedIds,
  setSelectedIds,
  pallets,
  showDivision,
  uploadData,
  setUploadData,
  uploadDivision,
}) => {
  const [alert, setAlert] = useState(null);

  const handleProcessRowUpdateError = (error) => {
    setAlert(<Error content={error.message} onChange={setAlert} />);
  };

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <InventoryGrid
          customerCode={customerCode}
          warehouseCode={warehouseCode}
          inventory={inventory}
          setInventory={setInventory}
          detail={detail}
          setDetail={setDetail}
          setAlert={setAlert}
          loadEnabled={loadEnabled}
          setLoadEnabled={setLoadEnabled}
          handleProcessRowUpdateError={handleProcessRowUpdateError}
          customColumn={customColumn}
          setZone={setZone}
          setLoc={setLoc}
          l={l}
          p={p}
          setL={setL}
          setSKU={setSKU}
          errorItems={errorItems}
          setErrorItems={setErrorItems}
          zoneOpt={zoneOpt}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          pallets={pallets}
          fromWarehouseCode={fromWarehouseCode}
        />
        {alert}
        <PreviewGrid
          inventory={inventory}
          setInventory={setInventory}
          detail={detail}
          setDetail={setDetail}
          warehouseCode={warehouseCode}
          fromWarehouseCode={fromWarehouseCode}
        />
        {showDivision && uploadDivision && (
          <TransferDivisionGroup
            details={detail}
            uploadData={uploadData}
            setUploadData={setUploadData}
          />
        )}
      </MDBox>
    </Card>
  );
};

const InventoryGrid = ({
  customerCode,
  warehouseCode,
  fromWarehouseCode,
  inventory,
  setInventory,
  detail,
  setDetail,
  setAlert,
  handleProcessRowUpdateError,
  loadEnabled,
  setLoadEnabled,
  customColumn,
  setZone,
  setLoc,
  l,
  p,
  setL,
  setSKU,
  errorItems,
  setErrorItems,
  zoneOpt,
  selectedIds,
  setSelectedIds,
  pallets,
}) => {
  const [formValues, setFormValues] = useState({ ...defaultDetailValues });
  const [headerZone, setHeaderZone] = useState(null);
  const [headerLocation, setHeaderLocation] = useState(null);

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(stockTransferDetailValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    reset,
  } = form;

  useEffect(() => {
    reset();
  }, [warehouseCode]);

  const [zoneID, locationID, vendorCode, skuCode, cartonID, palletNbr, holdPallet] = watch([
    "zoneID",
    "locationID",
    "vendorCode",
    "skuCode",
    "cartonID",
    "palletNbr",
    "holdPallet",
  ]);

  const detailProps = {
    control,
    errors,
    formRender: detailRender,
    watch,
    warehouseCode: fromWarehouseCode,
  };

  const inventoryReq = {
    warehouseCode: fromWarehouseCode,
    customerCode,
    zoneID,
    locationID,
    vendorCode,
    skuCode,
    palletNbr,
    cartonID,
    holdPallet: holdPallet.value,
    toWarehouseCode: warehouseCode,
    type: "StockTransfer",
  };

  const { isFetching } = useQuery({
    queryKey: [
      "stock-transfer-inventory-list",
      fromWarehouseCode,
      customerCode,
      zoneID,
      locationID,
      vendorCode,
      skuCode,
      palletNbr,
      cartonID,
      holdPallet,
      warehouseCode,
      loadEnabled,
    ],
    queryFn: () => getCommonInventoryList(inventoryReq),
    enabled: loadEnabled,
    onSuccess: (data) => {
      const list = data.data.data;
      if (Boolean(list.inventory)) {
        if (list.inventory.length === 0) {
          setAlert(
            <Error_
              content="No pallets found, might be occupied by Physical Count CCFreeze, Replenishment, Putaway"
              onChange={setAlert}
            />
          );
          setInventory([]);
        } else {
          let tempLocList = l;
          for (var zoneLoc of list.locOpt) {
            if (tempLocList[zoneLoc.zoneID] === undefined) {
              tempLocList = { ...tempLocList, [zoneLoc.zoneID]: zoneLoc.locationOpt }
            }
          }
          const inventoryList = list.inventory
            .map((x) => {
              const { id, quantityAvailable, zoneID, locationOpt, ...remain } = x;
              const newId = id + "-1";
              return {
                ...remain,
                id: newId,
                locId: id,
                lineNo: 1,
                zoneID: zoneID,
                toZone: fromWarehouseCode === warehouseCode ? zoneID : null,
                toLocation: null,
                quantityAvailable: quantityAvailable,
                transferQuantity: quantityAvailable,
                toPallet: null,
                toCartonID: null,
                toMLN: null,
                toExpiryDate: null,
              };
            })
            .filter((x) => !detail.find((y) => x.id === y.id));
          setInventory(inventoryList);
          setL(tempLocList);
        }
      }
      else {
        if (list.length === 0) {
          setAlert(
            <Error_
              content="No pallets found, might be occupied by Physical Count CCFreeze, Replenishment, Putaway"
              onChange={setAlert}
            />
          );
          setInventory([]);
        } else {
          let tempLocList = l;
          const inventoryList = list
            .map((x) => {
              const { id, quantityAvailable, zoneID, locationOpt, ...remain } = x;
              if (tempLocList[zoneID] === undefined) {
                tempLocList = { ...tempLocList, [zoneID]: locationOpt };
              }
              const newId = id + "-1";
              return {
                ...remain,
                id: newId,
                locId: id,
                lineNo: 1,
                zoneID: zoneID,
                toZone: fromWarehouseCode === warehouseCode ? zoneID : null,
                toLocation: null,
                quantityAvailable: quantityAvailable,
                transferQuantity: quantityAvailable,
                toPallet: null,
                toCartonID: null,
                toMLN: null,
                toExpiryDate: null,
              };
            })
            .filter((x) => !detail.find((y) => x.id === y.id));
          setInventory(inventoryList);
          setL(tempLocList);
        }
      }

      setLoadEnabled(false);
    },
  });

  const onSubmit = () => {
    setLoadEnabled(true);
  };

  const handleRowUpdate = (updatedRow, originalRow) => {
    if (JSON.stringify(updatedRow) !== JSON.stringify(originalRow)) {
      if (!selectedIds.includes(updatedRow.id)) {
        setSelectedIds([...selectedIds, updatedRow.id]);
      }
      const newInventory = inventory.map((inv) => {
        if (inv.id === updatedRow.id) {
          if (originalRow.toZone !== updatedRow.toZone) {
            setZone(updatedRow.toZone);
            if (
              updatedRow.toZone === null ||
              !l[updatedRow.toZone] ||
              (originalRow.toLocation !== null &&
                !l[updatedRow.toZone].some((x) => x === originalRow.toLocation))
            ) {
              updatedRow.toLocation = null;
            }
            if (
              updatedRow.toLocation === null ||
              !p[updatedRow.toLocation] ||
              (originalRow.toPallet !== null &&
                !p[updatedRow.toLocation][updatedRow.skuCode].some(
                  (x) => x === originalRow.toPallet
                ))
            ) {
              updatedRow.toPallet = null;
            }
          }

          const existingRowForCurrent = inventory.filter((x) => x.locId == updatedRow.locId);
          const totalQuantity = existingRowForCurrent
            .filter((x) => x.id !== originalRow.id)
            .reduce((accumulator, currentValue) => {
              return accumulator + currentValue.transferQuantity;
            }, 0);

          if (
            originalRow.toLocation !== updatedRow.toLocation &&
            updatedRow.toLocation !== null &&
            updatedRow.toLocation !== ""
          ) {
            setZone(originalRow.toZone);
            setLoc(updatedRow.toLocation);
            setSKU([updatedRow.skuCode]);
            if (
              originalRow.toPallet === null ||
              !p[updatedRow.toLocation] ||
              !p[updatedRow.toLocation][originalRow.skuCode].some((x) => x === originalRow.toPallet)
            ) {
              if (
                originalRow.id === existingRowForCurrent.at(-1).id &&
                (updatedRow.toLocation !== originalRow.locationID ||
                  updatedRow.toZone !== originalRow.zoneID) &&
                originalRow.quantityOnHold === 0 && originalRow.quantityAvailable + originalRow.quantityOnHold == originalRow.quantity
              )
                updatedRow.toPallet = "Source";
              else updatedRow.toPallet = null;
            } else if (
              originalRow.locationID === updatedRow.toLocation &&
              originalRow.zoneID === updatedRow.toZone
            ) {
              updatedRow.toPallet = null;
            }
          } else if (
            originalRow.toLocation !== updatedRow.toLocation &&
            (updatedRow.toLocation === null || updatedRow.toLocation === "")
          ) {
            if (originalRow.toPallet !== null) {
              updatedRow.toPallet = null;
            }
          }

          if (updatedRow.toPallet !== originalRow.toPallet) {
            if (updatedRow.toPallet === "New" || updatedRow.toPallet === null) {
              updatedRow.toCartonID = null;
              updatedRow.toMLN = null;
              updatedRow.toExpiryDate = null;
              if (updatedRow.toPallet === null) {
                updatedRow.transferQuantity = originalRow.quantityAvailable - totalQuantity;
              }
            } else if (updatedRow.toPallet === "Source") {
              updatedRow.toCartonID = originalRow.cartonID;
              updatedRow.toMLN = originalRow.mln;
              updatedRow.toExpiryDate = originalRow.expiryDate
                ? dayjs(originalRow.expiryDate, "DD / MM / YYYY").toDate()
                : null;
            } else if (updatedRow.toPallet !== null) {
              const destinationPallet = pallets[originalRow.toLocation][originalRow.skuCode].find(
                (plt) => plt.palletNbr === updatedRow.toPallet
              );
              if (destinationPallet) {
                updatedRow.toCartonID = destinationPallet.cartonID;
                updatedRow.toMLN = destinationPallet.mln;
                updatedRow.toExpiryDate = destinationPallet.expiryDate
                  ? dayjs(originalRow.expiryDate, "DD / MM / YYYY").toDate()
                  : null;
              } else {
                updatedRow.toCartonID = null;
                updatedRow.toMLN = null;
                updatedRow.toExpiryDate = null;
              }
            }
          }

          if (updatedRow.toPallet === "Source" && originalRow.toPallet !== "Source") {
            updatedRow.transferQuantity = originalRow.quantityAvailable - totalQuantity;
          }
          if (updatedRow.transferQuantity < 0) {
            updatedRow.transferQuantity = 0;
          }
          if (originalRow.quantityAvailable - totalQuantity - updatedRow.transferQuantity >= 0) {
            if (
              originalRow.quantityAvailable - totalQuantity - updatedRow.transferQuantity > 0 &&
              updatedRow.toPallet === "Source"
            ) {
              updatedRow.toPallet = null;
            }
            originalRow.transferQuantity = updatedRow.transferQuantity;
            inv.transferQuantity = updatedRow.transferQuantity;
          } else {
            originalRow.transferQuantity = originalRow.quantityAvailable - totalQuantity;
            updatedRow.transferQuantity = originalRow.quantityAvailable - totalQuantity;
            inv.transferQuantity = originalRow.quantityAvailable - totalQuantity;
          }

          originalRow.toZone = updatedRow.toZone;
          originalRow.toLocation = updatedRow.toLocation;
          originalRow.toPallet = updatedRow.toPallet;
          originalRow.toCartonID = updatedRow.toCartonID;
          originalRow.toMLN = updatedRow.toMLN;
          originalRow.toExpiryDate = updatedRow.toExpiryDate;
          inv.toZone = updatedRow.toZone;
          inv.toLocation = updatedRow.toLocation;
          inv.toPallet = updatedRow.toPallet;
          inv.toCartonID = updatedRow.toCartonID;
          inv.toMLN = updatedRow.toMLN;
          inv.toExpiryDate = updatedRow.toExpiryDate;
        }
        return inv;
      });
      setInventory(newInventory);
    }
    return originalRow;
  };

  const saveDetail = () => {
    const newDetail = inventory.filter(
      (x) =>
        x.toZone !== null &&
        x.toZone != "" &&
        x.toLocation !== null &&
        x.toLocation != "" &&
        x.transferQuantity !== null &&
        x.transferQuantity !== 0 &&
        x.toPallet !== null &&
        x.toPallet != ""
    );

    const unsuccessfulDetail = inventory.filter((x) =>
      selectedIds.some(y => y === x.id) && !newDetail.some(y => y.id === x.id)).map(x => {
        let msg = [];
        if (!x.toZone) msg.push('To Zone')
        if (!x.toLocation) msg.push('To Location')
        if (!x.transferQuantity) msg.push('Transfer Quantity')
        if (!x.toPallet) msg.push('To Pallet')
        return x.skuCode + ': No ' + msg.join(', ')
      });

    if (unsuccessfulDetail.length > 0) {
      const errorMsg = (
        <>
          {unsuccessfulDetail.map((line, index) => (
            <span key={index}>
              {line}
              <br />
            </span>
          ))}
        </>
      );
      setAlert(<Error_ content={errorMsg} onChange={setAlert} />);
      if (newDetail.length === 0) {
        return;
      }
    }
    const crossPalletDetail = newDetail.filter(
      (x) =>
        newDetail.some((y) => y.palletNbr === x.toPallet) ||
        newDetail.some((y) => y.toPallet === x.palletNbr) ||
        detail.some((y) => y.toPallet === x.palletNbr) ||
        detail.some((y) => y.palletNbr === x.toPallet)
    );
    if (crossPalletDetail.length > 0) {
      setAlert(<Error content="Cross Pallet error" onChange={setAlert} />);
      //highlight error row
      setErrorItems(crossPalletDetail);
      return;
    }

    //if added line with lineNo 1 transferquantity = 0
    //prompt error and return
    const noFirstLineDetail = newDetail.filter(
      (x) => !newDetail.some((y) => y.locId === x.locId && y.lineNo === 1)
    );
    if (noFirstLineDetail.length > 0) {
      setAlert(<Error content="Cannot transfer without parent" onChange={setAlert} />);
      //highlight error row
      setErrorItems(noFirstLineDetail);
      return;
    }

    //group by parent, if not total but selected source
    const grouped = newDetail.reduce((acc, det) => {
      if (!acc[det.locId]) {
        acc[det.locId] = [];
      }
      acc[det.locId].push(det);
      return acc;
    }, {});
    let errors = [];
    Object.values(grouped).forEach((x) => {
      const totalQty = x.reduce((acc, det) => {
        return acc + det.transferQuantity;
      }, 0);
      if (
        x.some((y) => y.toPallet === "Source") &&
        (totalQty !== x[0].quantityAvailable || x[0].quantityOnHold !== 0)
      ) {
        errors = [...errors, ...x];
      }
    });
    if (errors.length > 0) {
      setAlert(<Error content="Not full pallet on transferred source" onChange={setAlert} />);
      setErrorItems(errors);
      return;
    }

    if (detail.length > 0) {
      let modifiedDetail = deepCopy(detail);

      const filteredInv = newDetail.filter((x) => !detail.some((y) => y.id === x.id));

      modifiedDetail = [...modifiedDetail, ...filteredInv];

      setDetail(modifiedDetail);
    } else {
      setDetail(newDetail);
    }

    setErrorItems([]);

    const leftInventory = inventory.filter(
      (x) =>
        !(
          x.toZone !== null &&
          x.toZone != "" &&
          x.toLocation !== null &&
          x.toLocation != "" &&
          x.transferQuantity !== null &&
          x.transferQuantity !== 0 &&
          x.toPallet !== null &&
          x.toPallet != ""
        )
    );
    if (leftInventory.length < inventory.length) {
      setInventory(leftInventory);
    }
  };

  const selectRow = (id) => {
    if (selectedIds.length > id.length) {
      const removed = selectedIds.filter((x) => !id.includes(x));
      let updatedRow = inventory;
      //if main
      removed.forEach((removedID) => {
        if (removedID.endsWith("-1")) {
          updatedRow = updatedRow
            .filter(
              (x) =>
                x.id === removedID ||
                x.id.substring(0, x.id.length - 2) !== removedID.substring(0, removedID.length - 2)
            )
            .map((x) => {
              if (removed.includes(x.id)) {
                return {
                  ...x,
                  toZone: fromWarehouseCode === warehouseCode ? x.zoneID : null,
                  toLocation: null,
                  transferQuantity: x.quantityAvailable,
                  toPallet: null,
                  toCartonID: null,
                  toMLN: null,
                  toExpiryDate: null,
                };
              } else return x;
            });
        } else {
          //if child
          //uncheck = remove
          updatedRow = updatedRow.filter((x) => x.id !== removedID);
        }
      });
      setInventory(updatedRow);
    } else {
      const insert = id.filter((x) => !selectedIds.includes(x));
      const skus = inventory.filter((x) => insert.includes(x.id)).map((x) => x.skuCode);
      setSKU(skus);
      let updatedRow = inventory;
      insert.forEach((insertID) => {
        updatedRow = updatedRow.map((x) => {
          if (x.id === insertID) {
            const existingRowForCurrent = inventory.filter((y) => x.locId == y.locId);
            const totalQuantity = existingRowForCurrent
              .filter((y) => y.id !== x.id)
              .reduce((accumulator, currentValue) => {
                return accumulator + currentValue.transferQuantity;
              }, 0);
            const checking =
              (x.zoneID !== headerZone || x.locationID !== headerLocation) &&
              inventory.filter((y) => y.locId === x.locId).at(-1).id === x.id &&
              !existingRowForCurrent.some((x) => x.toPallet === "Source") &&
              x.quantityOnHold === 0 &&
              x.quantityOnHold + x.quantityAvailable === x.quantity;
            return {
              ...x,
              toZone: headerZone ?? x.toZone,
              toLocation: headerLocation ?? null,
              transferQuantity: x.quantityAvailable - totalQuantity,
              toPallet: checking ? "Source" : null,
              toCartonID: checking ? x.cartonID : null,
              toMLN: checking ? x.mln : null,
              toExpiryDate:
                checking && x.expiryDate ? dayjs(x.expiryDate, "DD / MM / YYYY").toDate() : null,
            };
          } else return x;
        });
      });
      setInventory(updatedRow);
    }
    setSelectedIds(id);
  };

  const applyAll = () => {
    const childrenSelected = selectedIds.filter((x) => !x.endsWith("-1"));
    const updatedRow = inventory
      .filter((x) => !childrenSelected.includes(x.id))
      .map((x) => {
        if (selectedIds.includes(x.id)) {
          if (!(x.zoneID === headerZone && x.locationID === headerLocation))
            return {
              ...x,
              toZone: headerZone,
              toLocation: headerLocation,
              transferQuantity: x.quantityAvailable,
              toPallet: x.quantityOnHold === 0 && x.quantityAvailable + x.quantityOnHold == x.quantity ? "Source" : null,
              toCartonID: x.quantityOnHold === 0 ? x.cartonID : null,
              toMLN: x.quantityOnHold === 0 ? x.mln : null,
              toExpiryDate:
                x.quantityOnHold === 0
                  ? x.expiryDate
                    ? dayjs(x.expiryDate, "DD / MM / YYYY").toDate()
                    : null
                  : null,
            };
          else
            return {
              ...x,
              toZone: headerZone,
              toLocation: headerLocation,
              transferQuantity: x.quantityAvailable,
              toPallet: null,
              toCartonID: null,
              toMLN: null,
              toExpiryDate: null,
            };
        } else return x;
      });
    setInventory(updatedRow);
  };

  return (
    <Fieldset label={"Stock Transfer Detail"}>
      {(isFetching) && <LoadingAlert />}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <StockTransferDetailInfo {...detailProps} />
        <MDBox mt={2} width="100%" display="flex" justifyContent="center">
          <MDButton
            type="submit"
            variant="gradient"
            color="info"
            disabled={customerCode === null || warehouseCode === null}
          >
            Load
          </MDButton>
        </MDBox>
      </form>
      <FormTitle title="Edit Stock Transfer Detail" />
      <MDBox mt={1.625}>
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <Autocomplete
              sx={{ "&.MuiAutocomplete-root .MuiOutlinedInput-root": { padding: "4.5px" } }}
              renderInput={(params) => <TextField {...params} label="To Zone" name="toZone" />}
              options={zoneOpt}
              value={headerZone}
              onChange={(e, option) => {
                setHeaderZone(option);
                setZone(option);
                setHeaderLocation(null);
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <Autocomplete
              sx={{ "&.MuiAutocomplete-root .MuiOutlinedInput-root": { padding: "4.5px" } }}
              renderInput={(params) => (
                <TextField {...params} label="To Location" name="toLocation" />
              )}
              options={Boolean(headerZone) ? l[headerZone] : []}
              value={headerLocation}
              onChange={(e, option) => {
                setHeaderLocation(option);
                setLoc(option);
              }}
            />
          </Grid>
          <Grid item>
            <MDButton
              color="info"
              onClick={applyAll}
              disabled={!(Boolean(headerZone) && Boolean(headerLocation))}
            >
              Apply All
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>
      <MDTypography mt={1.625} mb={2} color="info" variant="h5">
        Inventory Datagrid Display
      </MDTypography>
      <div style={{ height: 500, width: "100%" }}>
        <EditableStyledDataGrid
          autoHeight={false}
          columns={customColumn}
          rows={inventory}
          checkboxSelection={true}
          disableRowSelectionOnClick={true}
          onRowSelectionModelChange={selectRow}
          rowSelectionModel={selectedIds}
          sx={{
            borderRadius: "0.75rem",
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          getRowHeight={() => "auto"}
          processRowUpdate={handleRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          hideFooterSelectedRowCount={true}
          isCellEditable={isCellEditable}
          getRowClassName={(params) => {
            return errorItems.some((x) => x.id === params.row.id) ? "highlight" : "";
          }}
        />
      </div>
      <MDBox mt={2} width="100%" display="flex" justifyContent="center">
        <MDButton variant="gradient" color="info" onClick={saveDetail}>
          Add
        </MDButton>
      </MDBox>
    </Fieldset>
  );
};

const PreviewGrid = ({ inventory, setInventory, detail, setDetail, warehouseCode, fromWarehouseCode }) => {
  const addBackInventory = (req) => {
    const existingRowForCurrent = detail
      .filter((x) => x.locId == req.locId)
      .sort((a, b) => {
        return a.lineNo - b.lineNo;
      });

    const updatedInventory = [...inventory, ...existingRowForCurrent];
    setInventory(updatedInventory);
  };

  const handleDeleteClick = (req) => {
    //move back whole pallet, including current tranferQuantity and pallet and so on, not just per transfer
    addBackInventory(req);

    setDetail(detail.filter((row) => !(row.locId === req.locId)));
  };

  const previewColumn = deepCopy(inventoryColumns).map((x) => {
    if (x.field === "actions") {
      x.getActions = ({ row }) => {
        return [
          <>
            {row.lineNo === 1 && (
              <DeleteButton
                title="Delete"
                key="delete-button"
                onClick={() => {
                  handleDeleteClick(row);
                }}
              />
            )}
          </>,
        ];
      };
    }
    if (x.field === "fromWarehouseCode") {
      x.renderCell = (params) => {
        return <div>{fromWarehouseCode}</div>;
      }
    }
    if (x.field === "warehouseCode") {
      x.renderCell = (params) => {
        return <div>{warehouseCode}</div>;
      }
    }
    return x;
  });

  const caption = () => {
    const totalQuantity =
      detail.length > 0
        ? detail.reduce((total, det) => {
          return total + det.transferQuantity;
        }, 0)
        : 0;
    return <StockTransferCaption totalRows={detail.length} totalQuantity={totalQuantity} />;
  };

  return (
    <MDBox p={3}>
      <MDTypography mt={1.625} mb={2} color="info" variant="h5">
        Stock Transfer Detail Preview
      </MDTypography>
      <div style={{ height: 500, width: "100%" }}>
        <EditableStyledDataGrid
          autoHeight={false}
          columns={previewColumn}
          rows={detail}
          sx={{
            borderRadius: "0.75rem",
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          getRowHeight={() => "auto"}
          hideFooterSelectedRowCount={true}
          slots={{
            footer: caption,
          }}
        />
      </div>
    </MDBox>
  );
};

export default AddStockTransfer;
